import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, switchMap, of, map, catchError, first, shareReplay, throwError } from 'rxjs';
import { ApiResponse, MsgInit } from '../interface/response-tasa-agent-API.interface';
import { python, python_tasa } from 'src/environments/environments';
import { Binary } from '@angular/compiler';
import { SanitizerService } from 'src/app/core/services/sanitizer.service';

// ========== INTERFACES (sin cambios) ==========
interface N8nAudioResponse {
  success: boolean;
  message: string;
  data: {
    output: string;
    base64?: string;
    mimeType?: string;
  };
}

interface N8nAudioRequest {
  sessionId: string;
  pregunta: string;
  audio_enabled: boolean;
}

interface TokenValidationResult {
  success: boolean;
  message: string;
  data?: any;
  error?: any;
}

interface TokenConfiguration {
  clientId: string;
  audioEnabled: boolean;
  welcomeMessage: string;
  isPermanent: boolean;
  origin?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MessageAssistanceService {

  private readonly api_url_db: string = `${python.url_base}`;
  private readonly api_url_base: string = `${python.url_asis}${python.catia}`;
  private readonly api_url_db1: string = `${python_tasa.url_asis}`;
  private readonly jwt_token: string = `${python_tasa.url_base}`;

  constructor(
    private http: HttpClient,
    private sanitizer: SanitizerService
  ) { }

  // ========== MÉTODOS EXISTENTES (sin cambios) ==========

  initialMessage(): Observable<{ success: boolean, message: string, data?: MsgInit }> {
    const headers = { 'Content-Type': 'application/json' };
    const urlResource = `${this.api_url_db}/getRandom`;

    return this.http.get<{ success: boolean, message: string, data?: MsgInit }>(urlResource, { headers }).pipe(
      switchMap(response => {
        if (response.success) {
          sessionStorage.removeItem('msgInit');
          return of({ success: true, message: 'Asistentes cargados correctamente', data: response.data });
        } else {
          return of({ success: false, message: response.message });
        }
      }),
      catchError(error => {
        return of({ success: false, message: error.message });
      })
    );
  }

  sendMessage(
    ind: number,
    message: string,
    idCliente: string,
    threadId: string,
    audio_nom: string,
    volumeUp: string,
    humanOn: string,
    espqr: string,
    asistenteIdOpenIa: string,
    instrucciones: string,
    id_asistente: string,
    empresa_modelo: string,
    ruta_script: string,
    archivoBase64: string = 'no'
  ): Observable<ApiResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    const sanitizedMessage = this.sanitizer.sanitizeString(message);
    const sanitizedCliente = this.sanitizer.sanitizeString(idCliente);
    const sanitizedInstrucciones = this.sanitizer.sanitizeString(instrucciones);

    let body = `ind=${ind}&asistente=${encodeURIComponent(asistenteIdOpenIa)}&pregunta=${encodeURIComponent(sanitizedMessage)}` +
    `&idcliente=${encodeURIComponent(sanitizedCliente)}&audio_nom=${audio_nom}&volume_up=${volumeUp}&human_on=${humanOn}` +
    `&thread_id=${threadId}&ruta=no&espqr=${espqr}&instrucciones=${encodeURIComponent(sanitizedInstrucciones)}` +
    `&id_asistente=${id_asistente}&empresa_modelo=${empresa_modelo}&ruta_script=${ruta_script}` +
    `&archivo=${encodeURIComponent(archivoBase64)}`;

    return this.http.post<ApiResponse>(this.api_url_base, body, { headers }).pipe(
      first(),
      shareReplay(1),
      switchMap(response => {
        if (response.Exito === 'Exito') {
          return of(response);
        } else {
          return of({
            message: response.Exito || 'Unknown error',
            Respuesta: '',
            thread_id: ''
          });
        }
      }),
      catchError(error => {
        return of({
          message: error.message || 'Error desconocido',
          Respuesta: '',
          thread_id: ''
        });
      })
    );
  }

  sendAudioToBackend(audioBlob: Blob, filename: string = 'audio_message.webm'): Observable<any> {
    // ========== CAMBIO 1: Usar endpoint TASA con JWT ==========
    const api_iaChat = `${this.api_url_db1}/transcribir_tasa`;  // ✅ Endpoint TASA

    // ========== CAMBIO 2: Obtener token JWT ==========
    const token = this.getEmbedToken();

    if (!token) {
        console.error('❌ No hay token disponible para transcripción');
        return throwError(() => new Error('Token de autenticación requerido para transcripción'));
    }

    // ========== CAMBIO 3: Preparar FormData con 'file' (no 'audio') ==========
    const formData = new FormData();
    formData.append('file', audioBlob, filename);  // ✅ IMPORTANTE: 'file' no 'audio'

    // ========== CAMBIO 4: Headers con JWT + device info ==========
    const isIOS = /iPad|iPhone|iPod|Mac/.test(navigator.userAgent);
    const headers = new HttpHeaders({
        'Authorization': `Bearer ${token}`,  // ✅ JWT requerido
        'X-Device-Type': isIOS ? 'ios' : 'other',
        'X-Audio-Format': audioBlob.type
        // ❌ ELIMINAR 'X-Keep-Case' (no se usa en el nuevo endpoint)
    });

    // ========== CAMBIO 5: Enviar petición con manejo de errores mejorado ==========
    return this.http.post(api_iaChat, formData, { headers }).pipe(
        map((response: any) => {
            // Validar estructura de respuesta
            if (!response || typeof response !== 'object') {
                throw new Error('Respuesta inválida del servidor de transcripción');
            }

            // Retornar respuesta normalizada
            return response;
        }),
        catchError(error => {
            console.error('❌ Error en transcripción:', error);

            // Manejo específico de errores de autenticación
            if (error.status === 401) {
                console.error('❌ Token inválido o expirado para transcripción');
                sessionStorage.removeItem('embed_token');
                return throwError(() => new Error('Tu sesión ha expirado. Por favor recarga la página.'));
            }

            if (error.status === 403) {
                console.error('❌ Sin permisos de audio');
                return throwError(() => new Error('No tienes permisos para usar la transcripción de audio.'));
            }

            if (error.status === 429) {
                console.error('❌ Rate limit excedido en transcripción');
                return throwError(() => new Error('Demasiadas solicitudes. Por favor espera un momento.'));
            }

            if (error.status === 0) {
                console.error('❌ Sin conexión con servidor de transcripción');
                return throwError(() => new Error('No se pudo conectar con el servidor de transcripción.'));
            }

            // Error genérico
            const errorMessage = error.error?.message || error.message || 'Error desconocido en transcripción';
            return throwError(() => new Error(`Error de transcripción: ${errorMessage}`));
        })
    );
  }


  // ========== MÉTODO PRINCIPAL: sendMessageN8n2 ==========
  sendMessageN8n2(data: N8nAudioRequest): Observable<N8nAudioResponse> {
    // ========== PASO 1: OBTENER TOKEN ==========
    const token = this.getEmbedToken();

    if (!token) {
        console.error('❌ No hay token de autorización disponible');
        return of({
            success: false,
            message: 'Token de autorización no encontrado',
            data: { output: 'Error: No se pudo autenticar la solicitud. Por favor recarga la página.' }
        });
    }

    // ========== PASO 2: CONFIGURAR HEADERS CON TOKEN ==========
    const headers = new HttpHeaders()
        .set('Content-Type', 'application/json')
        .set('Authorization', `Bearer ${token}`);

    const urlResource: string = `${this.api_url_db1}/chat_tasa_ia`;

    // ========== PASO 3: VALIDACIONES BÁSICAS ==========
    if (!data || !data.sessionId || !data.pregunta) {
        return of({
            success: false,
            message: 'Faltan campos sessionId o pregunta',
            data: { output: 'Error: Datos incompletos enviados al servidor.' }
        });
    }

    if (!urlResource || urlResource.includes('undefined') || urlResource.includes('null')) {
        return of({
            success: false,
            message: 'URL del endpoint mal configurada',
            data: { output: 'Error de configuración del servidor.' }
        });
    }

    // ========== PASO 4: SANITIZAR DATOS ==========
    const sanitizedData: N8nAudioRequest = {
        sessionId: this.sanitizer.sanitizeString(data.sessionId),
        pregunta: this.sanitizer.sanitizeString(data.pregunta),
        audio_enabled: data.audio_enabled
    };

    // ========== PASO 5: ENVIAR PETICIÓN CON AUTENTICACIÓN ==========
    return this.http.post<any>(urlResource, sanitizedData, { headers }).pipe(
        map(response => {
            if (!response) {
                throw new Error('Respuesta vacía de N8N');
            }

            if (!response.success) {
                throw new Error(response.message || 'N8N response without success');
            }

            const processedResponse: N8nAudioResponse = {
                success: true,
                message: response.message || 'Respuesta exitosa',
                data: {
                    output: this.extractOutput(response),
                    base64: this.extractBase64Audio(response),
                    mimeType: this.extractMimeType(response)
                }
            };

            return processedResponse;
        }),
        catchError(error => {
            // ========== MANEJO ESPECÍFICO DE ERRORES DE AUTENTICACIÓN ==========
            if (error.status === 401) {
                console.error('❌ Error 401: Token no proporcionado o mal formado');
                sessionStorage.removeItem('embed_token');

                return of({
                    success: false,
                    message: 'Token de autenticación requerido',
                    data: {
                        output: ' Error de autenticación. Por favor recarga la página para obtener un nuevo token.'
                    }
                });
            }

            if (error.status === 403) {
                console.error('❌ Error 403: Token inválido, expirado o origen no permitido');
                sessionStorage.removeItem('embed_token');

                return of({
                    success: false,
                    message: 'Token inválido o expirado',
                    data: {
                        output: '⏱️ Tu sesión ha expirado o el token es inválido. Por favor recarga la página.'
                    }
                });
            }

            const errorAnalysis = this.analyzeN8nError(error, urlResource);

            return of({
                success: false,
                message: errorAnalysis.userMessage,
                data: {
                    output: 'Lo siento, hubo un problema procesando tu mensaje. Por favor intenta de nuevo en unos momentos.'
                }
            });
        })
    );
  }

  // ========== MÉTODO AUXILIAR: OBTENER TOKEN ==========
  private getEmbedToken(): string | null {
    const tokenFromStorage = sessionStorage.getItem('embed_token');
    if (tokenFromStorage && tokenFromStorage.length > 20) {
        return tokenFromStorage;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');

    if (tokenFromUrl && tokenFromUrl.length > 20) {
        sessionStorage.setItem('embed_token', tokenFromUrl);
        return tokenFromUrl;
    }

    console.warn('No se encontró token embed válido');
    return null;
  }

  // ========== MÉTODOS DE VALIDACIÓN DE TOKEN ==========

  validateEmbeddedToken(token: string): Observable<TokenValidationResult> {
    const validateUrl = `${this.jwt_token}/validate-embed-token`;

    if (token && token.length > 20) {
        sessionStorage.setItem('embed_token', token);
    }

    return this.validateTokenJWT(validateUrl, token).pipe(
        map((response: any) => {
            if (response && response.success === 1 && response.data && response.data.valid) {
                return {
                    success: true,
                    message: 'Token válido',
                    data: response.data
                };
            } else {
                console.warn(' Token inválido, limpiando sessionStorage');
                sessionStorage.removeItem('embed_token');
                return {
                    success: false,
                    message: response?.message || 'Token inválido o expirado',
                    data: null
                };
            }
        }),
        catchError((error: any) => {
            console.error('❌ Error validando token, limpiando sessionStorage');
            sessionStorage.removeItem('embed_token');

            return of({
                success: false,
                message: this.getTokenValidationErrorMessage(error),
                data: null,
                error: error
            });
        })
    );
  }

  processTokenConfiguration(tokenData: any): TokenConfiguration {
    const config: TokenConfiguration = {
      clientId: tokenData?.clientId?.toString() || 'no',
      audioEnabled: tokenData?.config?.audioEnabled || false,
      welcomeMessage: tokenData?.welcomeMessage || 'Hola! Soy tu asistente de TASA. ¿En qué puedo ayudarte hoy?',
      isPermanent: tokenData?.permanent || false,
      origin: tokenData?.config?.origin
    };

    return config;
  }

  private getTokenValidationErrorMessage(error: any): string {
    if (error?.status === 0) {
      return 'Sin conexión con el servidor de validación.';
    } else if (error?.status === 404) {
      return 'Servicio de validación no encontrado.';
    } else if (error?.status === 401) {
      return 'Token inválido o expirado.';
    } else if (error?.status === 403) {
      return 'Acceso denegado por origen no autorizado.';
    } else if (error?.status === 429) {
      return 'Demasiadas solicitudes de validación. Intenta más tarde.';
    } else if (error?.status && error.status >= 500) {
      return 'Error interno del servidor de validación.';
    } else {
      return error?.message || 'Error al validar el token de acceso.';
    }
  }

  validateTokenJWT(url: string, token: string): Observable<any> {
    const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    });

    const payload = {
        token: token,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        referrer: document.referrer || 'direct'
    };

    return this.http.post<any>(url, payload, { headers }).pipe(
        map((response: any) => {
            if (!response) {
                throw new Error('Respuesta vacía del servidor');
            }

            return {
                success: response.success || 0,
                message: response.message || 'Token procesado',
                data: response.data || null
            };
        }),
        catchError((error: any) => {
            const errorResponse = {
                success: 0,
                message: this.getJWTErrorMessage(error),
                data: null,
                error: {
                    status: error?.status || 0,
                    message: error?.message || 'Error desconocido',
                    url: url
                }
            };

            return throwError(() => errorResponse);
        })
    );
  }

  // ========== MÉTODOS AUXILIARES (sin cambios) ==========

  private extractOutput(response: any): string {
    const possibleOutputs = [
      response.data?.output,
      response.output,
      response.data?.text,
      response.text,
      response.data?.message,
      response.message
    ];

    for (const output of possibleOutputs) {
      if (output && typeof output === 'string' && output.trim().length > 0) {
        return output.trim();
      }
    }

    return 'Sin respuesta disponible del asistente.';
  }

  private extractBase64Audio(response: any): string | undefined {
    const possibleBase64 = [
      response.data?.base64,
      response.base64,
      response.data?.audio,
      response.audio,
      response.data?.audioBase64,
      response.audioBase64
    ];

    for (const base64 of possibleBase64) {
      if (base64 && typeof base64 === 'string' && base64.length > 100) {
        return base64;
      }
    }

    return undefined;
  }

  private extractMimeType(response: any): string | undefined {
    const possibleMimeTypes = [
      response.data?.mimeType,
      response.data?.mimetype,
      response.data?.mime_type,
      response.mimeType,
      response.mimetype,
      response.mime_type,
      response.data?.audioType,
      response.audioType
    ];

    for (const mimeType of possibleMimeTypes) {
      if (mimeType && typeof mimeType === 'string') {
        return mimeType;
      }
    }

    const hasAudio = this.extractBase64Audio(response);
    if (hasAudio) {
      return 'audio/mp3';
    }

    return undefined;
  }

  private analyzeN8nError(error: any, urlResource: string): {
    userMessage: string,
    technicalDetails: string
  } {
    let userMessage = 'Error de comunicación con el asistente';
    let technicalDetails = `Error: ${error.message || 'Desconocido'}`;

    if (error.status === 0) {
      userMessage = 'Sin conexión con el servidor';
      technicalDetails = `No se puede conectar a N8N en: ${urlResource}`;
    } else if (error.status === 404) {
      userMessage = 'Servicio no disponible temporalmente';
      technicalDetails = `Endpoint no encontrado: ${urlResource}`;
    } else if (error.status === 500) {
      userMessage = 'Error interno del servidor';
      technicalDetails = 'Error interno de N8N (posible error en workflow)';
    } else if (error.status >= 400 && error.status < 500) {
      userMessage = 'Error en la petición';
      technicalDetails = `Error del cliente: ${error.status} - ${error.message}`;
    } else if (error.status >= 500) {
      userMessage = 'Error del servidor';
      technicalDetails = `Error del servidor: ${error.status} - ${error.message}`;
    }

    return { userMessage, technicalDetails };
  }

  private getJWTErrorMessage(error: any): string {
    if (error?.status === 0) {
        return 'No se puede conectar con el servicio de validación';
    } else if (error?.status === 401) {
        return 'Token inválido o expirado';
    } else if (error?.status === 403) {
        return 'Acceso denegado por origen no autorizado';
    } else if (error?.status === 404) {
        return 'Servicio de validación no encontrado';
    } else if (error?.status === 429) {
        return 'Demasiadas solicitudes, intenta más tarde';
    } else if (error?.status && error.status >= 500) {
        return 'Error interno del servidor de validación';
    } else {
        return `Error de validación (${error?.status || 'desconocido'})`;
    }
  }

  generateSessionId(): string {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2);
  }

  getOrCreateSessionId(): string {
    let sessionId = sessionStorage.getItem('chat_session_id');
    if (!sessionId) {
      sessionId = this.generateSessionId();
      sessionStorage.setItem('chat_session_id', sessionId);
    }
    return sessionId;
  }

  // ========== NUEVO MÉTODO: Limpiar sesión ==========
  clearSession(): void {
    sessionStorage.removeItem('embed_token');
    sessionStorage.removeItem('chat_session_id');
  }
}
